import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Mic, MicOff, Phone, PhoneOff, ThumbsUp, ThumbsDown, Bot,
  Plus, Search, MoreVertical, Pin, PinOff, Pencil, Trash2,
  Copy, RotateCcw, Square, Check, X, PanelLeft, MessageSquare, ClipboardCheck
} from 'lucide-react';

import { chatApi } from '../../api/chat.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { Composer } from '../../components/ai/Composer.jsx';
import { MessageBubble } from '../../components/ai/MessageBubble.jsx';
import { VoiceOrb } from '../../components/ai/VoiceOrb.jsx';
import { TranscriptPanel } from '../../components/ai/TranscriptPanel.jsx';
import { useRealtimeVoice } from '../../hooks/useRealtimeVoice.js';
import { fmt } from '../../lib/formatters.js';
import { t } from '../../lib/i18n.js';

// Capability chips: routeToAgent (the internal handoff) is intentionally absent.
const TOOL_LABEL = {
  searchProducts: 'Ricerca prodotti',
  getInventory: 'Inventario',
  searchKnowledge: 'Knowledge base',
  getStoreInfo: 'Info negozio',
  compareProducts: 'Confronto prodotti'
};

const AGENT_LABEL = {
  orchestrator: 'Orchestratore',
  customer: 'Agente Cliente',
  inventory: 'Agente Inventario',
  consultant: 'Agente Consulente',
  procedure: 'Agente Procedure'
};

function uniqueToolLabels(names) {
  const seen = [];
  for (const n of names || []) {
    const label = TOOL_LABEL[n];
    if (label && !seen.includes(label)) seen.push(label);
  }
  return seen;
}

const DAY = 86400000;
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); }
function dateGroupLabel(dateStr) {
  const today = startOfDay(new Date());
  const d = startOfDay(dateStr || Date.now());
  if (d >= today) return 'Oggi';
  if (d >= today - DAY) return 'Ieri';
  if (d > today - 7 * DAY) return 'Ultimi 7 giorni';
  return 'Più vecchie';
}

// Split pinned conversations out, then bucket the rest by recency, preserving
// the server order (pinned desc, lastMessageAt desc).
function buildGroups(convs) {
  const pinned = [];
  const buckets = new Map();
  for (const c of convs) {
    if (c.pinned) { pinned.push(c); continue; }
    const label = dateGroupLabel(c.lastMessageAt || c.createdAt);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(c);
  }
  const groups = [];
  if (pinned.length) groups.push({ label: 'Fissate', items: pinned, pinnedGroup: true });
  for (const [label, items] of buckets) groups.push({ label, items });
  return groups;
}

export function ChatPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canChat = hasPermission('ai:chat');
  const canFeedback = hasPermission('ai:feedback');

  const [mode, setMode] = useState('text');
  const [agentId, setAgentId] = useState('orchestrator'); // 'orchestrator' = auto-routing
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [search, setSearch] = useState('');
  const [menuFor, setMenuFor] = useState(null);   // conversation id with open kebab menu
  const [renaming, setRenaming] = useState(null);  // { id, value }
  const [editing, setEditing] = useState(null);    // { id, value } message being edited
  const [deleteTarget, setDeleteTarget] = useState(null); // conversation pending deletion
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  const voice = useRealtimeVoice();
  const voiceLive = voice.status === 'live' || voice.status === 'connecting';

  // A voice session is persisted as a conversation by the broker. Refresh the
  // list once its id is known and again when the session ends, so the spoken
  // chat shows up alongside the typed ones (re-read, feedback, continue).
  useEffect(() => {
    if (voice.conversationId) queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
  }, [voice.conversationId, queryClient]);
  useEffect(() => {
    if (voice.status === 'closed' && voice.conversationId) {
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', voice.conversationId] });
    }
  }, [voice.status, voice.conversationId, queryClient]);

  const conversationsQuery = useQuery({
    queryKey: ['ai', 'conversations', search],
    enabled: canChat,
    queryFn: () => chatApi.listConversations({ limit: 50, q: search.trim() || undefined })
  });

  const agentsQuery = useQuery({ queryKey: ['ai', 'agents'], enabled: canChat, queryFn: chatApi.agents });
  const voiceConfigQuery = useQuery({
    queryKey: ['ai', 'voice-config'],
    enabled: canChat,
    queryFn: chatApi.voiceConfig,
    staleTime: 30_000
  });
  const voiceEnabled = Boolean(voiceConfigQuery.data && voiceConfigQuery.data.enabled);
  const agentOptions = useMemo(() => {
    const list = (agentsQuery.data && agentsQuery.data.items) || [];
    const specialists = list.filter((a) => a && a.id !== 'orchestrator' && a.enabled !== false);
    const opts = [{ value: 'orchestrator', label: 'Automatico (orchestratore)' }];
    if (specialists.length) {
      for (const a of specialists) opts.push({ value: a.id, label: a.label || AGENT_LABEL[a.id] || a.id });
    } else {
      for (const id of ['customer', 'inventory', 'consultant', 'procedure']) opts.push({ value: id, label: AGENT_LABEL[id] });
    }
    return opts;
  }, [agentsQuery.data]);

  const conversationQuery = useQuery({
    queryKey: ['ai', 'conversation', conversationId],
    enabled: Boolean(conversationId),
    queryFn: () => chatApi.getConversation(conversationId)
  });

  // Hydrate messages from a loaded conversation: fold tool-only turns away and
  // attach their tool names + the answering agent to the final reply.
  useEffect(() => {
    if (!conversationQuery.data) return;
    const raw = conversationQuery.data.messages || conversationQuery.data.items || [];
    const out = [];
    const fb = {};
    let toolBuf = [];
    for (const m of raw) {
      if (m.role === 'user') {
        out.push({ id: m._id, role: 'user', content: m.content });
        toolBuf = [];
      } else if (m.role === 'assistant') {
        if (Array.isArray(m.toolCalls) && m.toolCalls.length) {
          toolBuf.push(...m.toolCalls.map((c) => c.name).filter(Boolean));
        }
        const text = (m.content || '').trim();
        if (text) {
          out.push({
            id: m._id, role: 'assistant', content: m.content, tools: toolBuf,
            agentLabel: m.agentId ? (AGENT_LABEL[m.agentId] || m.agentId) : null,
            suggestions: Array.isArray(m.suggestions) ? m.suggestions : [],
            widgets: Array.isArray(m.widgets) ? m.widgets : []
          });
          if (m.feedback && m.feedback.thumbs) fb[m._id] = m.feedback.thumbs;
          toolBuf = [];
        }
      }
    }
    setMessages(out);
    setFeedbackGiven(fb);
  }, [conversationQuery.data]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const convs = useMemo(() => (conversationsQuery.data && conversationsQuery.data.items) || [], [conversationsQuery.data]);
  const groups = useMemo(() => buildGroups(convs), [convs]);
  const activeConv = convs.find((c) => c._id === conversationId) || null;

  const patchLastAssistant = (patch) => setMessages((prev) => {
    const next = [...prev];
    const last = next[next.length - 1];
    if (last && last.role === 'assistant') next[next.length - 1] = typeof patch === 'function' ? patch(last) : { ...last, ...patch };
    return next;
  });

  // Core send over the streaming transport. `convId` lets a regenerate/edit
  // target the same conversation explicitly.
  const sendText = async (text, convId = conversationId) => {
    if (!text || sending) return;
    setSending(true);
    setMessages((prev) => [...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true, tools: [] }
    ]);
    const controller = new AbortController();
    abortRef.current = controller;
    let stopped = false;
    try {
      const done = await chatApi.chatStream(
        { message: text, conversationId: convId || undefined, agentId: agentId || undefined },
        {
          signal: controller.signal,
          onAgent: (info) => patchLastAssistant((last) => ({ ...last, agentLabel: info.agent ? info.agent.label : last.agentLabel })),
          onDelta: (chunk) => patchLastAssistant((last) => ({ ...last, content: last.content + chunk }))
        }
      );
      patchLastAssistant((last) => ({
        ...last,
        id: done && done.messageId,
        content: (done && done.reply) || last.content,
        agentLabel: done && done.agent ? done.agent.label : last.agentLabel,
        tools: done && Array.isArray(done.toolTrace) ? done.toolTrace.map((tr) => tr.name).filter(Boolean) : last.tools,
        suggestions: done && Array.isArray(done.suggestions) ? done.suggestions : [],
        widgets: done && Array.isArray(done.widgets) ? done.widgets : [],
        streaming: false
      }));
      const newId = done && done.conversationId;
      if (newId && newId !== conversationId) setConversationId(newId);
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      // Refetch the conversation so every message gets its persisted id (needed
      // for edit/regenerate/feedback) and the canonical tool/agent metadata.
      if (newId) queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', newId] });
    } catch (err) {
      if (err && (err.name === 'AbortError' || err.code === 'STREAM_ABORTED')) {
        stopped = true;
        patchLastAssistant((last) => ({ ...last, streaming: false, content: last.content || '(interrotto)' }));
      } else {
        toast.error(err instanceof ApiError ? err.message : (err.message || t('common.error')));
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && next[next.length - 1].streaming) next.pop();
          if (next.length && next[next.length - 1].role === 'user') next.pop();
          return next;
        });
        setDraft(text);
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      if (stopped) { /* keep partial answer */ }
    }
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    sendText(text);
  };

  const stopGeneration = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const startNew = () => {
    setConversationId(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  // Truncate at a message (server + local) then resend `text`.
  const truncateAndSend = async (messageId, text) => {
    if (!conversationId || !messageId) return;
    try {
      await chatApi.truncate(conversationId, messageId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common.error'));
      return;
    }
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.id === messageId);
      return i >= 0 ? prev.slice(0, i) : prev;
    });
    await sendText(text);
  };

  const regenerate = async () => {
    if (sending) return;
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUser || !lastUser.id) { toast.error('Rigenerazione non disponibile per questo messaggio.'); return; }
    await truncateAndSend(lastUser.id, lastUser.content);
  };

  const submitEdit = async () => {
    if (!editing) return;
    const text = (editing.value || '').trim();
    const target = editing;
    setEditing(null);
    if (!text || !target.id) return;
    await truncateAndSend(target.id, text);
  };

  const copyMessage = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success('Risposta copiata.'); }
    catch { toast.error('Copia non riuscita.'); }
  };

  const feedbackMutation = useMutation({
    mutationFn: (body) => chatApi.feedback(body),
    onSuccess: (_data, body) => {
      setFeedbackGiven((prev) => ({ ...prev, [body.messageId]: body.thumbs }));
      toast.success('Grazie, feedback registrato.');
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => chatApi.updateConversation(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] }),
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => chatApi.deleteConversation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      if (id === conversationId) startNew();
      setDeleteTarget(null);
      toast.success('Conversazione eliminata.');
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const closeMutation = useMutation({
    mutationFn: (id) => chatApi.closeConversation(id),
    onSuccess: (data) => {
      const n = (data && data.tasksCreated) || 0;
      toast.success(n > 0 ? `Conversazione chiusa: ${n} attività create.` : 'Conversazione chiusa. Nessuna attività da creare.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      queryClient.invalidateQueries({ queryKey: ['operations', 'summary'] });
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const togglePin = (c) => { setMenuFor(null); updateMutation.mutate({ id: c._id, body: { pinned: !c.pinned } }); };
  const beginRename = (c) => { setMenuFor(null); setRenaming({ id: c._id, value: c.title || '' }); };
  const commitRename = () => {
    if (!renaming) return;
    const value = (renaming.value || '').trim();
    const id = renaming.id;
    setRenaming(null);
    if (value) updateMutation.mutate({ id, body: { title: value } });
  };
  const confirmDelete = (c) => { setMenuFor(null); setDeleteTarget(c); };

  const onModeChange = (next) => {
    if (next !== 'voice' && voiceLive) voice.stop();
    setMode(next);
  };
  const startVoice = () => voice.start();

  // If voice gets disabled for staff while the voice tab is open, drop back to
  // text and tear down any live session.
  useEffect(() => {
    if (!voiceEnabled && mode === 'voice') {
      if (voiceLive) voice.stop();
      setMode('text');
    }
  }, [voiceEnabled, mode, voiceLive, voice]);

  const openConversation = (id) => { setConversationId(id); setSidebarOpen(false); };

  if (!canChat) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-sm text-ink-muted">Permesso <code>ai:chat</code> richiesto.</p>
      </div>
    );
  }

  const lastAssistantId = [...messages].reverse().find((m) => m.role === 'assistant' && !m.streaming)?.id;

  const sidebar = (
    <div className="flex h-full w-72 shrink-0 flex-col border-r border-border bg-surface">
      <div className="flex flex-col gap-2 border-b border-border p-3">
        <Button onClick={startNew} className="w-full justify-center">
          <Plus className="h-4 w-4" /> Nuova conversazione
        </Button>
        {voiceEnabled && (
          <Tabs value={mode} onChange={onModeChange} items={[{ value: 'text', label: 'Testo' }, { value: 'voice', label: 'Voce' }]} />
        )}
        {mode === 'text' && (
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">Agente</span>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-ink focus:border-ink focus:outline-none"
            >
              {agentOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        )}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca conversazioni..."
            className="w-full rounded-md border border-border bg-surface-elevated py-1.5 pl-8 pr-2 text-xs text-ink placeholder:text-ink-faint focus:border-ink focus:outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {conversationsQuery.isLoading ? (
          <div className="flex flex-col gap-2 p-1">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : convs.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-ink-muted">
            {search ? 'Nessun risultato.' : 'Nessuna conversazione.'}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((group) => (
              <div key={group.label}>
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">{group.label}</p>
                <ul className="flex flex-col gap-0.5">
                  {group.items.map((c) => {
                    const active = c._id === conversationId;
                    const isRenaming = renaming && renaming.id === c._id;
                    return (
                      <li key={c._id} className="group relative">
                        {isRenaming ? (
                          <div className="flex items-center gap-1 px-1 py-1">
                            <input
                              autoFocus
                              value={renaming.value}
                              onChange={(e) => setRenaming({ id: c._id, value: e.target.value })}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                              className="w-full rounded border border-ink bg-surface-elevated px-2 py-1 text-xs text-ink focus:outline-none"
                            />
                            <button type="button" aria-label="Salva" onClick={commitRename} className="text-ink-muted hover:text-success"><Check className="h-4 w-4" /></button>
                            <button type="button" aria-label="Annulla" onClick={() => setRenaming(null)} className="text-ink-muted hover:text-danger"><X className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openConversation(c._id)}
                            className={clsx(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left',
                              active ? 'bg-border/60 text-ink' : 'text-ink-muted hover:bg-border/40'
                            )}
                          >
                            {c.pinned ? <Pin className="h-3 w-3 shrink-0 text-accent" /> : <MessageSquare className="h-3 w-3 shrink-0 text-ink-faint" />}
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs text-ink">{c.title || `Conv. ${String(c._id).slice(-6)}`}</span>
                              <span className="block text-[10px] text-ink-faint">{fmt.dateTime(c.lastMessageAt || c.createdAt)} - {c.messageCount || 0} msg</span>
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              aria-label="Azioni"
                              onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c._id ? null : c._id); }}
                              className="shrink-0 rounded p-0.5 text-ink-faint opacity-0 hover:bg-surface-subtle hover:text-ink group-hover:opacity-100"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </span>
                          </button>
                        )}
                        {menuFor === c._id && (
                          <>
                            <button type="button" className="fixed inset-0 z-10 cursor-default" aria-label="Chiudi menu" onClick={() => setMenuFor(null)} />
                            <div className="absolute right-1 top-9 z-20 w-40 rounded-md border border-border bg-surface-elevated p-1 shadow-elevated">
                              <button type="button" onClick={() => beginRename(c)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-ink hover:bg-surface-subtle"><Pencil className="h-3.5 w-3.5" /> Rinomina</button>
                              <button type="button" onClick={() => togglePin(c)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-ink hover:bg-surface-subtle">
                                {c.pinned ? <><PinOff className="h-3.5 w-3.5" /> Rimuovi pin</> : <><Pin className="h-3.5 w-3.5" /> Fissa in alto</>}
                              </button>
                              <button type="button" onClick={() => confirmDelete(c)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-danger hover:bg-danger/10"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
                            </div>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">{sidebar}</div>

      {/* Mobile sidebar drawer */}
      {sidebarOpen && (
        <>
          <button type="button" aria-label="Chiudi" onClick={() => setSidebarOpen(false)} className="fixed inset-0 z-30 bg-black/40 md:hidden" />
          <div className="fixed inset-y-0 left-0 z-40 md:hidden">{sidebar}</div>
        </>
      )}

      {/* Chat surface */}
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
          <button type="button" aria-label="Conversazioni" onClick={() => setSidebarOpen(true)} className="rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink md:hidden">
            <PanelLeft className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <Bot className="h-4 w-4 shrink-0 text-accent" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
            {mode === 'voice' ? 'Assistente vocale' : (activeConv ? (activeConv.title || 'Conversazione') : 'Nuova conversazione')}
          </span>
          {mode === 'text' && activeConv && (
            <>
              <button type="button" aria-label="Chiudi e genera attività" title="Chiudi e genera attività (alert/follow-up)" onClick={() => closeMutation.mutate(activeConv._id)} disabled={closeMutation.isPending} className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink disabled:opacity-50">
                <ClipboardCheck className="h-4 w-4" />
              </button>
              <button type="button" aria-label={activeConv.pinned ? 'Rimuovi pin' : 'Fissa'} onClick={() => updateMutation.mutate({ id: activeConv._id, body: { pinned: !activeConv.pinned } })} className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink">
                {activeConv.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              </button>
              <button type="button" aria-label="Rinomina" onClick={() => beginRename(activeConv)} className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink">
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" aria-label="Elimina" onClick={() => confirmDelete(activeConv)} className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger">
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </header>

        {mode === 'text' ? (
          <>
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
              {conversationQuery.isLoading && conversationId ? (
                <div className="mx-auto flex max-w-3xl flex-col gap-3">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-3/4" />)}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <div className="max-w-sm text-center">
                    <Bot className="mx-auto mb-3 h-8 w-8 text-accent" strokeWidth={1.5} />
                    <p className="text-sm text-ink-muted">Scrivi un messaggio per iniziare. Suggerimenti:</p>
                    <ul className="mt-3 flex flex-col gap-1.5">
                      {['Quale crocchetta consigli per cucciolo di Maltese di 3 mesi?', 'Cosa devo fare se un cliente vuole un rimborso?', 'Quante confezioni di X abbiamo in magazzino?'].map((s) => (
                        <li key={s}>
                          <button type="button" onClick={() => setDraft(s.replace(/"/g, ''))} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-ink-muted hover:border-ink hover:text-ink">
                            {s}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <ul className="mx-auto flex max-w-3xl flex-col gap-4">
                  {messages.map((m, idx) => {
                    const tools = m.role === 'assistant' ? uniqueToolLabels(m.tools) : [];
                    const agentLabel = m.role === 'assistant' ? m.agentLabel : null;
                    const given = m.id ? feedbackGiven[m.id] : null;
                    const isEditing = editing && editing.id === m.id;
                    const canRegen = m.role === 'assistant' && !m.streaming && m.id && m.id === lastAssistantId;
                    return (
                      <li key={m.id || idx} className="group/msg">
                        {isEditing ? (
                          <div className="ml-auto w-full max-w-[80%]">
                            <textarea
                              autoFocus
                              rows={2}
                              value={editing.value}
                              onChange={(e) => setEditing({ id: m.id, value: e.target.value })}
                              className="w-full rounded-2xl border border-ink bg-surface-elevated px-3 py-2 text-sm text-ink focus:outline-none"
                            />
                            <div className="mt-1 flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Annulla</Button>
                              <Button size="sm" onClick={submitEdit} disabled={!editing.value.trim()}>Invia</Button>
                            </div>
                          </div>
                        ) : (
                          <MessageBubble
                            role={m.role}
                            content={m.content}
                            streaming={m.streaming}
                            suggestions={idx === messages.length - 1 ? m.suggestions : undefined}
                            widgets={m.widgets}
                            onSuggestion={(s) => sendText(s)}
                          />
                        )}

                        {!isEditing && m.role === 'user' && m.id && (
                          <div className="mt-1 flex justify-end pr-1 opacity-0 transition-opacity group-hover/msg:opacity-100">
                            <button type="button" onClick={() => setEditing({ id: m.id, value: m.content })} className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink-muted">
                              <Pencil className="h-3 w-3" /> Modifica
                            </button>
                          </div>
                        )}

                        {m.role === 'assistant' && !m.streaming && (agentLabel || tools.length > 0 || (canFeedback && m.id) || canRegen || m.content) && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-11">
                            {canFeedback && m.id && (
                              <span className="flex items-center gap-1">
                                <button type="button" aria-label="Risposta utile" onClick={() => feedbackMutation.mutate({ messageId: m.id, thumbs: 'up' })} className={clsx('inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-surface-subtle', given === 'up' ? 'text-success' : 'text-ink-faint hover:text-ink-muted')}>
                                  <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.75} fill={given === 'up' ? 'currentColor' : 'none'} />
                                </button>
                                <button type="button" aria-label="Risposta non utile" onClick={() => feedbackMutation.mutate({ messageId: m.id, thumbs: 'down' })} className={clsx('inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-surface-subtle', given === 'down' ? 'text-danger' : 'text-ink-faint hover:text-ink-muted')}>
                                  <ThumbsDown className="h-3.5 w-3.5" strokeWidth={1.75} fill={given === 'down' ? 'currentColor' : 'none'} />
                                </button>
                              </span>
                            )}
                            {m.content && (
                              <button type="button" aria-label="Copia" onClick={() => copyMessage(m.content)} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-ink-faint transition-colors hover:bg-surface-subtle hover:text-ink-muted">
                                <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
                              </button>
                            )}
                            {canRegen && (
                              <button type="button" onClick={regenerate} disabled={sending} className="inline-flex items-center gap-1 rounded-md px-1.5 text-[11px] text-ink-faint transition-colors hover:bg-surface-subtle hover:text-ink-muted disabled:opacity-50">
                                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} /> Rigenera
                              </button>
                            )}
                            {agentLabel && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
                                <Bot className="h-3 w-3" strokeWidth={2} /> {agentLabel}
                              </span>
                            )}
                            {tools.map((label) => (
                              <span key={label} className="inline-flex items-center rounded-full border border-border bg-surface-subtle px-2 py-0.5 text-[11px] text-ink-muted">{label}</span>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="shrink-0 border-t border-border px-4 py-3 sm:px-6">
              <div className="mx-auto flex max-w-3xl items-end gap-2">
                <div className="flex-1">
                  <Composer value={draft} onChange={setDraft} onSubmit={submit} sending={sending} />
                </div>
                {sending && (
                  <Button variant="secondary" size="sm" onClick={stopGeneration} className="mb-1">
                    <Square className="h-3.5 w-3.5 fill-current" /> Ferma
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center gap-4 border-b border-border py-8">
              <VoiceOrb status={voice.status} speaking={voice.speaking} userSpeaking={voice.userSpeaking} />
              <div className="flex items-center gap-2">
                {voiceLive ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={voice.toggleMute}>
                      {voice.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      {voice.muted ? 'Microfono off' : 'Microfono on'}
                    </Button>
                    <Button variant="danger" size="sm" onClick={voice.stop}><PhoneOff className="h-4 w-4" /> Termina</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={startVoice}><Phone className="h-4 w-4" /> Avvia conversazione</Button>
                )}
              </div>
              {voice.error && <p className="text-xs text-danger">{voice.error}</p>}
            </div>
            <TranscriptPanel transcript={voice.transcript} />
          </>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        destructive
        title="Elimina conversazione"
        description={deleteTarget ? `Eliminare "${deleteTarget.title || 'senza titolo'}"? L'azione è irreversibile.` : ''}
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />
    </div>
  );
}
